import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { LimeMultiSelect } from "@/Components/LimeMultiSelect";
import { LimeTextInput } from "@/Components/LimeTextInput";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { useLingui } from "@lingui/react";
import { Button, Divider, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useState } from "react";
import {
  RiMoneyEuroCircleLine,
  RiSearch2Line,
  RiTimeLine,
} from "react-icons/ri";
import { Accordion, AccordionItem } from "@heroui/react";
import { TextVariant } from "@/types/text-variants";
import {
  formatCurrency,
  returnCurrencySymbol,
} from "../../../../../../../shared/utils/utils";
import { i18n } from "@lingui/core";
import useFeature from "@/hooks/useFeature";
import { AnimatePresence, motion } from "framer-motion";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { usePutUserServiceCustomValues } from "@/lib/api-client/paths/user";

type User = {
  userId: string;
  name: string;
};

type Service = {
  serviceId: string;
  name: string;
  price: number;
  currency: string;
  duration: number;
  timeOffStart?: number;
  timeOffDuration?: number;
};

const rolesSchema = z.object({
  roleName: z.string().min(1),
  userIds: z.array(z.string()).min(1),
  services: z.array(
    z.object({
      serviceId: z.string(),
      customDuration: z.number().nullable(),
      customPrice: z.number().nullable(),
      customTimeOffStart: z.number().nullable(),
      customTimeOffDuration: z.number().nullable(),
    }),
  ),
});

type FormValues = z.infer<typeof rolesSchema>;

type RolesModalProps = {
  initialValues: FormValues;
  onClose: () => void;
  users: User[];
  services: Service[];
  onSubmit: (values: FormValues) => void;
};

const RolesModal = ({
  initialValues,
  onClose,
  users,
  services,
  onSubmit,
}: RolesModalProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { isFeatureEnabled } = useFeature();
  const [search, setSearch] = useState("");

  const form = useForm<FormValues>({
    initialValues,
    validate: zodResolver(rolesSchema),
  });

  const handleSubmit = () => {
    const values = form.values;
    onSubmit(values);
  };

  const { locale } = i18n;

  return (
    <div className="flex flex-col gap-5">
      <Divider />
      <div>
        <LimeTextInput
          label={t`Ime vloge`}
          {...form.getInputProps("roleName")}
          placeholder={t`Vnesite ime vloge`}
        />
      </div>
      <div>
        <LimeMultiSelect
          label={t`Izberite osebje`}
          data={users.map((u) => ({
            value: u.userId.toString(),
            label: u.name,
          }))}
          {...form.getInputProps("userIds")}
          placeholder={t`Ni osebja`}
        />
      </div>
      <Divider />
      <div>
        <LimeTextInput
          label={t`Določite ceno in trajanje po meri`}
          placeholder={t`Išči storitve`}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </div>
      <Divider />

      <Accordion variant="bordered" className="w-full">
        {services
          ?.filter((s) => s.name?.toLowerCase().includes(search?.toLowerCase()))
          .map((service, index) => {
            const customServiceRow = form.values.services.find(
              (s) => s.serviceId === service.serviceId,
            );

            if (!customServiceRow) {
              form.insertListItem("services", {
                serviceId: service.serviceId,
                customDuration: null,
                customPrice: null,
                customTimeOffStart: null,
                customTimeOffDuration: null,
              });
              return null;
            }

            const duration =
              customServiceRow.customDuration ?? service.duration;
            const price = customServiceRow.customPrice ?? service.price;
            const isTimeOff = !!(
              customServiceRow.customTimeOffStart ??
              customServiceRow.customTimeOffDuration
            );

            return (
              <AccordionItem
                title={
                  <div className="flex justify-between">
                    <Text variant={TextVariant.Body}> {service.name}</Text>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1">
                        <RiTimeLine />
                        <Text variant={TextVariant.Caption}>
                          <span
                            className={
                              customServiceRow.customDuration !== null
                                ? "line-through"
                                : ""
                            }
                          >
                            {service.duration}
                          </span>
                          {customServiceRow.customDuration !== null && (
                            <span className="ml-1">{duration}</span>
                          )}{" "}
                          min
                        </Text>
                      </div>
                      <div className="flex items-center gap-1">
                        <RiMoneyEuroCircleLine />
                        <Text variant={TextVariant.Caption}>
                          <span
                            className={
                              customServiceRow.customPrice !== null
                                ? "line-through"
                                : ""
                            }
                          >
                            {formatCurrency({
                              amount: service.price,
                              currency: service.currency,
                              locale,
                            })}
                          </span>
                          {customServiceRow.customPrice !== null && (
                            <span className="ml-1">
                              {formatCurrency({
                                amount: price,
                                currency: service.currency,
                                locale,
                              })}
                            </span>
                          )}
                        </Text>
                      </div>
                    </div>
                  </div>
                }
                key={index}
                value={service.name}
                className={"accordion_item"}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Divider />
                    <div className="flex items-center justify-between">
                      <Text variant={TextVariant.BodyEmphasized}>
                        <Trans>Urejanje cene</Trans>
                      </Text>
                      <Button
                        variant={"subtle"}
                        size="xs"
                        onClick={() => {
                          form.setFieldValue(
                            `services.${index}.customPrice`,
                            null,
                          );
                        }}
                      >
                        <Trans>Ponastavi ceno</Trans>
                      </Button>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="flex shrink-0">
                        <Text variant={TextVariant.Body}>
                          <Trans>Redna cena:</Trans>{" "}
                          <span
                            className={
                              customServiceRow.customPrice !== null
                                ? "line-through"
                                : ""
                            }
                          >
                            {formatCurrency({
                              amount: service.price,
                              currency: service.currency,
                              locale,
                            })}
                          </span>
                        </Text>
                      </div>
                      <div>
                        <LimeSwitch
                          checked={customServiceRow.customPrice !== null}
                          title={t`Cena po meri`}
                          isCompact
                          onValueChange={(e) => {
                            if (e) {
                              form.setFieldValue(
                                `services.${index}.customPrice`,
                                service.price,
                              );
                            } else {
                              form.setFieldValue(
                                `services.${index}.customPrice`,
                                null,
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                    <LimeCollapse
                      isOpen={customServiceRow.customPrice !== null}
                    >
                      <div className="flex flex-col gap-2">
                        <Text variant={TextVariant.Body}>
                          <Trans>Cena po meri:</Trans>{" "}
                          {formatCurrency({
                            amount: price,
                            currency: service.currency,
                            locale,
                          })}
                        </Text>
                        <LimeNumberInput
                          step={1}
                          minValue={0}
                          label={t`Cena po meri`}
                          size="sm"
                          hideStepper
                          value={price}
                          onChange={(value) => {
                            if (typeof value !== "number") return;
                            form.setFieldValue(
                              `services.${index}.customPrice`,
                              value,
                            );
                          }}
                          endContent={
                            <Text variant={TextVariant.Caption}>
                              {returnCurrencySymbol({
                                currency: service.currency,
                              })}
                            </Text>
                          }
                        />
                      </div>
                    </LimeCollapse>
                  </div>

                  <Divider />

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="font-bold">
                        <Trans>Urejanje trajanja</Trans>
                      </span>
                      <Button
                        variant={"subtle"}
                        size="xs"
                        onClick={() => {
                          form.setFieldValue(
                            `services.${index}.customDuration`,
                            null,
                          );
                          form.setFieldValue(
                            `services.${index}.customTimeOffStart`,
                            null,
                          );
                          form.setFieldValue(
                            `services.${index}.customTimeOffDuration`,
                            null,
                          );
                        }}
                      >
                        <Trans>Ponastavi trajanje</Trans>
                      </Button>
                    </div>
                    <div className="flex w-full flex-col gap-4">
                      <Text variant={TextVariant.Body}>
                        <Trans>Redno trajanje:</Trans>{" "}
                        <span
                          className={
                            customServiceRow.customDuration !== null
                              ? "line-through"
                              : ""
                          }
                        >
                          {service.duration} min
                        </span>
                      </Text>
                      {service.timeOffDuration && service.timeOffStart ? (
                        <div className="flex w-full justify-between gap-4">
                          <div className="flex w-1/3 flex-col items-center rounded-md bg-gray-100 p-1">
                            <span className="text-sm text-gray-500">
                              <Trans>Začetno trajanje</Trans>:
                            </span>
                            {service.timeOffStart}
                          </div>
                          <div className="flex w-1/3 flex-col items-center rounded-md bg-gray-100 p-1">
                            <div className="text-sm text-gray-500">
                              <Trans>Trajanje brez izvajalca</Trans>:
                            </div>
                            {service.timeOffDuration}
                          </div>
                          <div className="flex w-1/3 flex-col items-center rounded-md bg-gray-100 p-1">
                            <div className="text-sm text-gray-500">
                              <Trans>Končno trajanje</Trans>:
                            </div>
                            {service.duration -
                              service.timeOffStart -
                              service.timeOffDuration}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-4">
                      <div className="flex gap-4">
                        <div className="flex flex-1 items-center">
                          <span>
                            <Trans>Trajanje po meri</Trans>:
                          </span>
                        </div>
                        <div className="w-fit">
                          <LimeSwitch
                            checked={customServiceRow.customDuration !== null}
                            title={t`Trajanje po meri`}
                            isCompact
                            onValueChange={(e) => {
                              if (e) {
                                form.setFieldValue(
                                  `services.${index}.customDuration`,
                                  service.duration,
                                );
                                form.setFieldValue(
                                  `services.${index}.customTimeOffStart`,
                                  service.timeOffStart,
                                );
                                form.setFieldValue(
                                  `services.${index}.customTimeOffDuration`,
                                  service.timeOffDuration,
                                );
                              } else {
                                form.setFieldValue(
                                  `services.${index}.customDuration`,
                                  null,
                                );
                              }
                            }}
                          />
                        </div>
                      </div>

                      <LimeCollapse
                        isOpen={customServiceRow.customDuration !== null}
                      >
                        <div className="flex flex-col gap-4">
                          <div className="flex gap-4">
                            <div className="flex flex-1 items-center">
                              <span>
                                <Trans>Trajanje po meri</Trans>:
                              </span>
                            </div>
                            {isFeatureEnabled("TIMEOFF") ? (
                              <LimeSwitch
                                className="flex-1"
                                title={t`Trajanje brez izvajalca`}
                                checked={isTimeOff}
                                onValueChange={(e) => {
                                  if (e) {
                                    form.setFieldValue(
                                      `services.${index}.customTimeOffStart`,
                                      service.timeOffStart || 1,
                                    );
                                    form.setFieldValue(
                                      `services.${index}.customTimeOffDuration`,
                                      service.timeOffDuration || 1,
                                    );
                                  } else {
                                    form.setFieldValue(
                                      `services.${index}.customTimeOffStart`,
                                      null,
                                    );
                                    form.setFieldValue(
                                      `services.${index}.customTimeOffDuration`,
                                      null,
                                    );
                                  }
                                }}
                              />
                            ) : null}
                          </div>

                          <div className="flex flex-col justify-between gap-4 md:flex-row md:gap-0">
                            <LimeNumberInput
                              label={
                                isTimeOff ? t`Začetno trajanje` : t`Trajanje`
                              }
                              step={1}
                              minValue={1}
                              className="max-w-1/4 md:w-fit"
                              value={
                                isTimeOff
                                  ? customServiceRow.customTimeOffStart
                                  : duration
                              }
                              size="sm"
                              onChange={(value) => {
                                if (typeof value !== "number") return;

                                if (isTimeOff) {
                                  form.setFieldValue(
                                    `services.${index}.customTimeOffStart`,
                                    value,
                                  );
                                } else {
                                  form.setFieldValue(
                                    `services.${index}.customDuration`,
                                    value,
                                  );
                                }
                              }}
                              endContent={
                                <Text variant={TextVariant.Caption}>min</Text>
                              }
                            />

                            <AnimatePresence>
                              {isTimeOff ? (
                                <motion.div
                                  initial={{ opacity: 0, width: "auto" }}
                                  animate={{ opacity: 1, width: "auto" }}
                                  exit={{ opacity: 0, width: "auto" }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <LimeNumberInput
                                    label={t`Trajanje brez izvajalca`}
                                    step={1}
                                    minValue={1}
                                    value={
                                      customServiceRow.customTimeOffDuration
                                    }
                                    size="sm"
                                    onChange={(value) => {
                                      if (typeof value !== "number") return;
                                      form.setFieldValue(
                                        `services.${index}.customTimeOffDuration`,
                                        value,
                                      );
                                    }}
                                    endContent={
                                      <Text variant={TextVariant.Caption}>
                                        min
                                      </Text>
                                    }
                                  />
                                </motion.div>
                              ) : null}
                            </AnimatePresence>

                            <AnimatePresence>
                              {isTimeOff ? (
                                <motion.div
                                  initial={{ opacity: 0, width: "auto" }}
                                  animate={{ opacity: 1, width: "auto" }}
                                  exit={{ opacity: 0, width: "auto" }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <LimeNumberInput
                                    label={t`Končno trajanje`}
                                    step={1}
                                    // minValue={1}
                                    value={
                                      duration -
                                      (customServiceRow.customTimeOffDuration ||
                                        0) -
                                      (customServiceRow.customTimeOffStart || 0)
                                    }
                                    size="sm"
                                    onChange={(value) => {
                                      if (typeof value !== "number") return;

                                      const totalDuration =
                                        value +
                                        (customServiceRow.customTimeOffDuration ||
                                          0) +
                                        (customServiceRow.customTimeOffStart ||
                                          0);

                                      form.setFieldValue(
                                        `services.${index}.customDuration`,
                                        totalDuration,
                                      );
                                    }}
                                    endContent={
                                      <Text variant={TextVariant.Caption}>
                                        min
                                      </Text>
                                    }
                                  />
                                </motion.div>
                              ) : null}
                            </AnimatePresence>
                          </div>
                        </div>
                      </LimeCollapse>
                    </div>
                  </div>
                </div>
              </AccordionItem>
            );
          })}
      </Accordion>

      <div className="flex justify-end gap-2">
        <Button variant="subtle" onClick={onClose}>
          <Trans>Prekliči</Trans>
        </Button>
        <Button onClick={handleSubmit}>
          <Trans>Shrani</Trans>
        </Button>
      </div>
    </div>
  );
};

export default RolesModal;
